import { pipe } from 'ramda';
import React, { FC, useMemo } from 'react';
import { OptionTypeBase } from 'react-select';
import styled from 'styled-components';
import { ReminderConfiguration } from 'api/timesheets/configuration/api-configuration';
import {
  options,
  ReminderOption,
} from 'api/timesheets/configuration/reminder-option';
import { Select } from 'components/atoms/select';
import { isEmpty, orElse } from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslation } from 'hooks/use-translation';
import { Box } from '../components';
import { ControlPanel } from '../control-panel';
import { SetState, State } from '../state';
import { useAfter } from './use-after';
import { useBefore } from './use-before';

const Input = styled(Select)`
  width: 150px;
`;

const reminderOptions = options().map((x) => ({
  label: x,
  value: x,
}));

interface Props {
  reminder: State<ReminderConfiguration>;
  onReminderChange: SetState<ReminderConfiguration>;
}

export const RemindersSettings: FC<Props> = ({
  reminder,
  onReminderChange,
}) => {
  const label = useTranslation('settings.timesheets.controls.reminders.label');
  const description = useTranslation(
    'settings.timesheets.controls.reminders.description',
  );
  const beforeSuffix = useTranslation(
    'settings.timesheets.controls.reminders.before.suffix',
  );
  const afterSuffix = useTranslation(
    'settings.timesheets.controls.reminders.after.suffix',
  );
  const [after, setAfter] = useAfter(reminder, onReminderChange);
  const [before, setBefore] = useBefore(reminder, onReminderChange);
  const loading = useMemo(
    () => pipe(maybeValueOf, isEmpty)(reminder),
    [reminder],
  );
  const selectedAfter = useMemo(
    () =>
      pipe(
        () => after,
        maybeValueOf,
        orElse<ReminderOption | null>(null),
        (x) => reminderOptions.find((o) => o.value === x),
      )(),
    [after],
  );
  const selectedBefore = useMemo(
    () =>
      pipe(
        () => before,
        maybeValueOf,
        orElse<ReminderOption | null>(null),
        (x) => reminderOptions.find((o) => o.value === x),
      )(),
    [before],
  );

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <Box>
        <Input
          disabled={loading}
          options={reminderOptions}
          value={selectedBefore}
          onChange={(selected: OptionTypeBase) => setBefore(selected.value)}
        />
        <>{beforeSuffix}</>
      </Box>
      <Box>
        <Input
          disabled={loading}
          options={reminderOptions}
          value={selectedAfter}
          onChange={(selected: OptionTypeBase) => setAfter(selected.value)}
        />
        <>{afterSuffix}</>
      </Box>
    </ControlPanel>
  );
};
